import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { useDeleteVacationHoldMutation } from 'api/accounts/vacationHoldsCrud'
import CancelButton from 'components/buttons/CancelButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HoldActionMenu from 'components/customer-details/content/holds/HoldActionMenu'
import HoldsFormDialog from 'components/customer-details/content/holds/HoldsFormDialog'
import VacationHoldList from 'components/customers/accounts/vacation-holds/VacationHoldList'
import { HOLD_FORM_MODES } from 'settings/constants/service'
import T from 'T'
import { handleError } from 'utils/error'

import type { HoldFormMode, HoldsCustomerDetails } from 'components/customer-details/content/holds/types'
import type { VacationHoldApi } from 'types/api/account/vacationHold'

type Props = {
  open: boolean
  row: { accountId: string; locationId?: string | null }
  onClose: () => void
}

const CONTENT_SX = { pr: 1.5 }
const ITEM_SX = { pr: 0 }
const ACTIONS_SX = { justifyContent: 'space-between' }
const CONFIRM_DIALOG_PROPS = { maxWidth: 'xs', fullWidth: true } as const

const VacationHoldDialog: React.FC<Props> = ({ open, row, onClose }) => {
  const { accountId, locationId = null } = row
  const [getAccountDetails, { data: accountData }] = useLazyGetAccountDetailsQuery()
  const [formMode, setFormMode] = useState<HoldFormMode>(HOLD_FORM_MODES.CREATE)
  const [openFormDialog, setOpenFormDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [actionAnchorEl, setActionAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedHold, setSelectedHold] = useState<VacationHoldApi | null>(null)
  const [deleteHold, { isLoading: isDeleteLoading }] = useDeleteVacationHoldMutation()
  const customerDetails: HoldsCustomerDetails | null = accountData ?? null
  const confirmButtonProps = useMemo(() => ({ loading: isDeleteLoading }), [isDeleteLoading])

  const handleCloseDeleteDialog = useCallback(() => setOpenDeleteDialog(false), [])

  const handleConfirmDelete = useCallback(() => {
    if (selectedHold) deleteHold({ vacationHoldId: selectedHold.id, accountId }).unwrap().catch(handleError)
    setOpenDeleteDialog(false)
  }, [accountId, deleteHold, selectedHold])

  const handleAddClick = useCallback(() => {
    setFormMode(HOLD_FORM_MODES.CREATE)
    setOpenFormDialog(true)
  }, [])

  const handleFormDialogClose = useCallback(() => {
    setOpenFormDialog(false)
    setFormMode(HOLD_FORM_MODES.CREATE)
  }, [])

  const handleOpenActionMenu = useCallback((event: React.MouseEvent<HTMLElement>, hold: VacationHoldApi) => {
    setSelectedHold(hold)
    setActionAnchorEl(event.currentTarget)
  }, [])

  const handleCloseActionMenu = useCallback(() => setActionAnchorEl(null), [])

  const handleEditActionClick = useCallback(() => {
    setFormMode(HOLD_FORM_MODES.UPDATE)
    setOpenFormDialog(true)
  }, [])

  const handleDeleteActionClick = useCallback(() => setOpenDeleteDialog(true), [])

  useEffect(() => {
    if (open && accountId) getAccountDetails({ accountId })
  }, [open, accountId, getAccountDetails])

  return (
    <HHBaseDialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <HHDialogTitle showCloseIcon title={locationId ? T.LOCATION_HOLDS : T.HOLDS} onClose={onClose} />
      <DialogContent sx={CONTENT_SX}>
        <VacationHoldList
          showActionMenu
          itemSx={ITEM_SX}
          locationId={locationId}
          open={open}
          row={row}
          onClickVacationHoldActionMenu={handleOpenActionMenu}
        />
        <HoldActionMenu
          accountId={accountId}
          anchorEl={actionAnchorEl}
          selectedHold={selectedHold}
          onClose={handleCloseActionMenu}
          onDelete={handleDeleteActionClick}
          onEdit={handleEditActionClick}
        />
        <HHConfirmDialog
          ConfirmButtonProps={confirmButtonProps}
          confirmButtonTitle={T.DELETE}
          confirmDescription={T.ARE_YOU_SURE_YOU_WANT_TO_CONTINUE}
          confirmTitle="Confirm hold deletion"
          DialogProps={CONFIRM_DIALOG_PROPS}
          isOpen={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
        />
        <HoldsFormDialog
          accountId={accountId}
          customerDetails={customerDetails}
          initialLocationId={locationId}
          mode={formMode}
          open={openFormDialog}
          selectedHold={selectedHold}
          onClose={handleFormDialogClose}
          onSave={handleFormDialogClose}
        />
      </DialogContent>
      <HHDialogActions sx={ACTIONS_SX}>
        <CancelButton label={T.CLOSE} onClick={onClose} />
        <Button size="small" variant="outlined" onClick={handleAddClick}>
          {T.ADD_HOLD}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

export default VacationHoldDialog
